/**
 * AI Narrative Generation Hook
 * 
 * This hook provides AI-assisted narrative generation for ESG report sections.
 * All generated content is tracked with full audit trail for compliance.
 * 
 * CRITICAL: Every AI generation is logged to ai_content_audit table
 * with is_ai_generated flag for greenwashing protection.
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * Hook for AI narrative generation
 */
export function useAINarrative() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  /**
   * Generate AI narrative for a report section
   * @param {Object} request - The generation request
   * @param {string} request.organisationId - Organisation ID
   * @param {string} request.sectionSlug - Section slug (e.g., 'governance-board-oversight')
   * @param {Object} request.contextData - Data context for AI
   * @param {string} [request.draftId] - Optional draft ID
   * @param {string} [request.customPrompt] - Optional custom prompt
   * @param {number} [request.temperature] - AI temperature (default 0.7)
   * @param {number} [request.maxTokens] - Max tokens (default 1024)
   * @returns {Promise<Object>} Generation result
   */
  const generateNarrative = useCallback(async (request) => {
    setIsGenerating(true);
    setError(null);

    try {
      // Get current session for auth
      const { data: { session } } = await supabase.auth.getSession();
      
      // Use the access token if available, otherwise fall back to anon key
      const authToken = session?.access_token || SUPABASE_ANON_KEY;
      const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-ai-narrative`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          organisation_id: request.organisationId,
          section_slug: request.sectionSlug,
          draft_id: request.draftId,
          context_data: request.contextData,
          custom_prompt: request.customPrompt,
          temperature: request.temperature || 0.7,
          max_tokens: request.maxTokens || 1024,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      const formattedResult = {
        success: result.success,
        content: result.content,
        auditId: result.audit_id,
        error: result.error,
        metadata: result.metadata ? {
          model: result.metadata.model,
          provider: result.metadata.provider,
          tokensUsed: result.metadata.tokens_used,
          generationDurationMs: result.metadata.generation_duration_ms,
        } : undefined,
      };

      setLastResult(formattedResult);
      return formattedResult;

    } catch (err) {
      const errorMessage = err.message || 'Failed to generate AI narrative';
      setError(errorMessage);
      
      const errorResult = {
        success: false,
        error: errorMessage,
      };
      setLastResult(errorResult);
      return errorResult;

    } finally {
      setIsGenerating(false);
    }
  }, []);

  /**
   * Build section content object with AI tracking metadata
   */
  const buildSectionContent = useCallback((content, aiResult) => {
    return {
      content,
      isAiGenerated: !!aiResult?.auditId,
      aiAuditId: aiResult?.auditId,
      generatedAt: aiResult?.auditId ? new Date().toISOString() : undefined,
      wasEdited: false,
    };
  }, []);

  /**
   * Mark section content as edited (for audit purposes)
   */
  const markAsEdited = useCallback((sectionContent) => {
    return {
      ...sectionContent,
      wasEdited: true,
      editedAt: new Date().toISOString(),
    };
  }, []);

  return {
    generateNarrative,
    buildSectionContent,
    markAsEdited,
    isGenerating,
    error,
    lastResult,
  };
}

/**
 * Context data builders for different section types
 */
export const contextBuilders = {
  /**
   * Build context for governance sections
   */
  governance: (data) => ({
    board_size: data.boardSize,
    has_sustainability_committee: data.sustainabilityCommittee,
    review_frequency: data.reviewFrequency || 'quarterly',
    responsible_officer: data.responsibleOfficer,
    responsible_officer_title: data.responsibleOfficerTitle,
  }),

  /**
   * Build context for emissions summary
   */
  emissions: (data) => ({
    scope_1_emissions: data.scope1,
    scope_2_location_based: data.scope2Location,
    scope_2_market_based: data.scope2Market,
    scope_3_emissions: data.scope3,
    total_emissions: data.totalEmissions,
    previous_year_total: data.previousYearTotal,
    year_over_year_change: data.previousYearTotal 
      ? ((data.totalEmissions - data.previousYearTotal) / data.previousYearTotal * 100).toFixed(1) + '%'
      : undefined,
    methodology: data.methodology || 'GHG Protocol',
    emission_categories: data.categories,
  }),

  /**
   * Build context for reduction targets
   */
  targets: (data) => ({
    baseline_year: data.baselineYear,
    baseline_emissions: data.baselineEmissions,
    target_year: data.targetYear,
    reduction_target_percent: data.targetPercent,
    current_emissions: data.currentEmissions,
    progress_percent: ((data.baselineEmissions - data.currentEmissions) / 
      (data.baselineEmissions * (data.targetPercent / 100)) * 100).toFixed(1),
    is_net_zero_target: data.isNetZero,
    interim_targets: data.interimTargets,
  }),

  /**
   * Build context for climate risks
   */
  risks: (data) => ({
    physical_risks: data.physicalRisks,
    transition_risks: data.transitionRisks,
    climate_opportunities: data.opportunities,
  }),

  /**
   * Build context for executive summary
   */
  executiveSummary: (data) => ({
    organisation_name: data.organisationName,
    financial_year: data.financialYear,
    total_gross_emissions: data.totalEmissions,
    total_net_emissions: data.netEmissions,
    target_progress: data.targetProgress,
    key_achievements: data.keyAchievements,
    key_risks_identified: data.keyRisks,
    outlook: data.outlook,
  }),

  /**
   * Build context for scenario analysis
   */
  scenarioAnalysis: (data) => ({
    scenarios_analysed: data.scenarios,
    methodology: data.methodology,
  }),
};

/**
 * Sample data for demo/testing
 */
export const sampleContextData = {
  governance: {
    boardSize: 5,
    sustainabilityCommittee: true,
    reviewFrequency: 'quarterly',
    responsibleOfficer: 'Jane Smith',
    responsibleOfficerTitle: 'Chief Sustainability Officer',
  },
  emissions: {
    scope1: 125.5,
    scope2Location: 450.2,
    scope2Market: 320.1,
    scope3: 1250.8,
    totalEmissions: 1826.5,
    previousYearTotal: 2105.3,
    methodology: 'GHG Protocol Corporate Standard',
    categories: [
      { name: 'Electricity', value: 450.2 },
      { name: 'Natural Gas', value: 85.3 },
      { name: 'Fleet Vehicles', value: 40.2 },
      { name: 'Business Travel', value: 520.4 },
    ],
  },
  targets: {
    baselineYear: 2020,
    baselineEmissions: 2500,
    targetYear: 2030,
    targetPercent: 50,
    currentEmissions: 1826.5,
    isNetZero: true,
  },
};

export default useAINarrative;
